import type {IUserInfoApiResData} from '@/apis/login';
import {getResolvedLoginContext, type ILoginContextInfo} from '@/apis/organizationContext';
import {clearStudentSessionCookies, setFixedOrganizationCookies} from './auth';

function isOrganizationId(value: unknown): value is string {
  return typeof value === 'string' && /^[1-9]\d*$/.test(value);
}

function hasMalformedAccountOrganizationId(value: unknown): boolean {
  return value !== undefined && !isOrganizationId(value);
}

/**
 * The server authenticates the student against the fixed entry's signed login
 * context. A globally unique learner may therefore belong to a different
 * organization than the page branding. Keep validating the issued context and
 * the server-returned organization before accepting that session.
 */
export function matchesFixedOrganizationSession(
  user: IUserInfoApiResData,
  loginContext: ILoginContextInfo | null,
): boolean {
  const organization = user.organization;
  if (
    !loginContext
    || typeof loginContext.login_context !== 'string'
    || !loginContext.login_context.trim()
    || !organization
    || !isOrganizationId(loginContext.organization_id)
    || !isOrganizationId(organization.organization_id)
  ) {
    return false;
  }

  const contextAccountOrganizationId = loginContext.account_organization_id;
  const userAccountOrganizationId = organization.account_organization_id;
  return !hasMalformedAccountOrganizationId(contextAccountOrganizationId)
    && !hasMalformedAccountOrganizationId(userAccountOrganizationId);
}

/**
 * Confirms that a fixed-entry login was authenticated with the server-issued
 * context before allowing callers to continue. Cookies retain the student's
 * actual organization, while the entry key retains its brand configuration.
 */
export function confirmFixedOrganizationSession(
  orgKey: string,
  user: IUserInfoApiResData,
  loginContext: ILoginContextInfo | null = getResolvedLoginContext(),
): boolean {
  if (!matchesFixedOrganizationSession(user, loginContext) || !user.organization) {
    clearStudentSessionCookies();
    return false;
  }

  setFixedOrganizationCookies(orgKey, user.organization.organization_id);
  return true;
}
